import React, { useState } from 'react';
import { Trophy, ChevronDown, ChevronRight } from 'lucide-react';
import BadgeIcon from './BadgeIcon';
import { fmtDateNo } from '../utils/dateFormat';

const LEVEL_STYLES = {
  nybegynner: { color: '#F97316', label: 'Nybegynner' },
  viderekommende: { color: '#0F172A', label: 'Viderekommende' },
  bronse: { color: '#B45309', label: 'Bronse' },
  solv: { color: '#94A3B8', label: 'Sølv' },
  gull: { color: '#EAB308', label: 'Gull' },
};

// Beholder navnet BadgeRing for bakoverkompatibilitet – rendrer nå det offisielle PNG-ikonet.
const BadgeRing = ({ level, size = 40 }) => (
  <BadgeIcon level={level} size={size} />
);

/**
 * Delt visning: viser progresjon inkl. alle nivåer med tellende resultater.
 * Brukes både i Klubb drift > Merkeskyting og på Hjem-siden for medlemmer.
 */
const ProgressionCard = ({ progression, defaultExpanded = false, compact = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const p = progression;
  return (
    <div className={`bg-gray-800 rounded-lg ${compact ? 'p-3' : 'p-4'}`} data-testid={`progression-card-${p.variant_key}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className={`text-white font-bold ${compact ? 'text-base' : 'text-lg'}`}>{p.discipline_name}</h3>
          <p className="text-gray-400 text-sm">{p.variant_name} • {p.total_results} registrerte serier</p>
        </div>
        {p.active_stage ? (
          <div className="flex items-center gap-2">
            <BadgeRing level={p.active_stage.level} size={compact ? 32 : 40} />
            <div className="text-right">
              <p className="text-white font-bold">{p.active_stage.level_name}</p>
              <p className="text-yellow-400 text-sm">{p.active_stage.count}/10 ({p.active_stage.percent}%)</p>
            </div>
          </div>
        ) : (
          <span className="text-green-400 font-bold flex items-center gap-1"><Trophy className="w-4 h-4" />Alle nivåer fullført</span>
        )}
      </div>

      {p.active_stage && (
        <div className="mt-3">
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-yellow-500 transition-all" style={{ width: `${p.active_stage.percent}%` }} />
          </div>
          <p className="text-xs text-gray-400 mt-1">Krav: <strong>{p.active_stage.threshold}</strong> {p.unit_label}</p>
        </div>
      )}

      <button className="mt-3 text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1"
        onClick={() => setExpanded(!expanded)} data-testid={`toggle-details-${p.variant_key}`}>
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {expanded ? 'Skjul detaljer' : 'Vis alle nivåer og resultater'}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {p.stages.map((stage, i) => (
            <div key={i} className="p-3 rounded-lg bg-gray-700/50">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <BadgeRing level={stage.level} size={24} />
                  <span className="text-white font-semibold">{stage.level_name}</span>
                  <span className="text-gray-400 text-xs">(krav {stage.threshold} {p.unit_label})</span>
                </div>
                <div className="text-sm">
                  {stage.status === 'achieved' && <span className="text-green-400 font-medium">✓ Oppnådd {fmtDateNo(stage.achieved_at)}</span>}
                  {stage.status === 'skipped' && <span className="text-slate-400 font-medium italic" title="Hoppet over via startpunkt-valg">⤻ Hoppet over</span>}
                  {stage.status === 'active' && <span className="text-yellow-400 font-medium">Pågår ({stage.count}/10)</span>}
                  {stage.status === 'waiting' && <span className="text-gray-500 font-medium">Venter</span>}
                </div>
              </div>
              {stage.qualifying_results && stage.qualifying_results.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-600">
                        <th className="px-2 py-1 text-left">#</th>
                        <th className="px-2 py-1 text-left">Dato</th>
                        <th className="px-2 py-1 text-right">Resultat</th>
                        <th className="px-2 py-1 text-left">Program</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stage.qualifying_results.map((r, j) => (
                        <tr key={j} className="text-gray-300">
                          <td className="px-2 py-1">{j + 1}</td>
                          <td className="px-2 py-1">{fmtDateNo(r.result_date)}</td>
                          <td className="px-2 py-1 text-right text-white font-semibold">{r.score}</td>
                          <td className="px-2 py-1 text-gray-400">{r.program || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { ProgressionCard, BadgeRing, LEVEL_STYLES };
export default ProgressionCard;
